import { useMatches } from "@remix-run/react";
import { useMemo } from "react";
import fs from 'fs';
import path from 'path';

import type { User } from "~/models/user.server";
import type { SessionUser } from "./modules/auth/authenticator";
import defineAbilityFor from "./modules/auth/authorization";
import { DateTime } from "luxon";
import { ReportData } from "./modules/report/ReportData";
import { ENV } from "~/config/env.server";
import { canonicalizeLocale, formatDateForUserLocale, formatDateTimeForUserLocale } from "~/utils/date-format";

const DEFAULT_REDIRECT = "/";

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
// eslint-disable-next-line no-undef
export function safeRedirect(to: FormDataEntryValue | string | null | undefined, defaultRedirect: string = DEFAULT_REDIRECT) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(id: string): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(() => matchingRoutes.find((route) => route.id === id), [matchingRoutes, id]);
  return route?.data;
}

function isUser(user: any): user is User {
  return user && typeof user === "object" && typeof user.email === "string";
}

export function useOptionalUser(): User | undefined {
  const data = useMatchesData("routes/dashboard");
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useOptionalSessionUser(): SessionUser | undefined {
  const data = useMatchesData("routes/dashboard");
  if (!data || !data.sessionUser) {
    return undefined;
  }
  return data?.sessionUser as SessionUser;
}

export function useUser(): User {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error("No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.");
  }
  return maybeUser;
}

export function validateEmail(email: unknown): email is string {
  return typeof email === "string" && email.length > 3 && email.includes("@");
}

export function getHost(request: Request) {
  return new URL(request.url).host;
}

/**
 * Gets the public-facing URL for email links.
 * Uses PUBLIC_URL environment variable if set, otherwise falls back to getHost(request).
 * This ensures email links always use the correct production domain.
 */
export function getPublicUrl(request: Request): string {
  if (ENV.PUBLIC_URL) {
    // Remove protocol if present, we'll add it in the email template
    return ENV.PUBLIC_URL.replace(/^https?:\/\//, "");
  }
  return getHost(request);
}

export function useAbility() {
  const data = useMatchesData("root");

  const sessionUser = (data?.sessionUser as SessionUser) ?? null;

  const ability = useMemo(() => {
    return defineAbilityFor(sessionUser);
  }, [sessionUser]);

  return ability;
}

export function getFormattedDate(date: Date): string {
  return formatDateTimeForUserLocale(date, Intl.DateTimeFormat().resolvedOptions().locale);
}

export function getClientLocale(request: Request): string {
  const clientLocale = request.headers.get("Cookie")?.match(/clientLocale=([a-z\-]+)/i);

  if (clientLocale) {
    return canonicalizeLocale(clientLocale[1]);
  }

  return "en-GB";
}

export function formatDateWithClientLocale(date: Date, clientLocale: string) {
  return DateTime.fromJSDate(date).setLocale(canonicalizeLocale(clientLocale)).toLocaleString();

  /*if (_isUSADateFormat(clientLocale)) {
    return `${_padZero(date.getMonth() + 1)}/${_padZero(date.getDate())} at ${_padZero(date.getHours())}:${_padZero(date.getMinutes())}`;
  } else {
    return `${_padZero(date.getDate())}/${_padZero(date.getMonth() + 1)} at ${_padZero(date.getHours())}:${_padZero(date.getMinutes())}`;
  }*/
}

export function formatToDisplayDate(dateString: string, locale?: string) {
  return formatDateForUserLocale(dateString, locale || Intl.DateTimeFormat().resolvedOptions().locale);
}

/**
 * Helper function to write report data to a JSON file
 * @param data The report data to write
 * @param filename Optional filename to write to (defaults to 'report.json')
 */
export function writeObjectToJson(data: ReportData, filename: string = 'report.json'): void {
  try {
    const filePath = path.join(__dirname, filename);

    // Convert the data to a JSON string with pretty formatting
    const jsonData = JSON.stringify(data, null, 2);

    // Write the data to the file
    fs.writeFileSync(filePath, jsonData, 'utf-8');

    console.log(`Report data successfully written to ${filePath}`);
  } catch (error) {
    console.error("Error writing report data to JSON file:", error);
  }
}